import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { formatCpfCnpj, formatPhone, isValidCpfCnpj } from "../lib/validators";
import { SEO } from "../components/SEO";

type TrialSignupConfig = {
  allowed_days?: number[];
  default_days?: number;
};

async function loadTrialSignupConfig(): Promise<{ allowed: number[]; defaultDays: number }> {
  const response = await fetch("/api/public/trial-signup-config", {
    headers: { Accept: "application/json" },
  });
  const result = await response.json().catch(() => null);
  if (!response.ok || !result?.success) {
    throw new Error(result?.message || "Não foi possível carregar as opções de teste grátis.");
  }

  const data = (result.data || {}) as TrialSignupConfig;
  const allowed = Array.isArray(data.allowed_days) && data.allowed_days.length > 0
    ? data.allowed_days.map((day) => Number(day)).filter((day) => [7, 15, 30].includes(day))
    : [7, 15, 30];
  const defaultDays = allowed.includes(Number(data.default_days))
    ? Number(data.default_days)
    : allowed[0];

  return { allowed, defaultDays };
}

export const Register = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    document: "",
    password: "",
    confirmPassword: "",
    trialDays: 7,
  });
  const [documentError, setDocumentError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [configError, setConfigError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [trialOptions, setTrialOptions] = useState<number[]>([]);
  const [loadingTrialConfig, setLoadingTrialConfig] = useState(true);

  useEffect(() => {
    let active = true;
    const bootstrap = async () => {
      try {
        const { allowed, defaultDays } = await loadTrialSignupConfig();
        if (!active) return;
        setTrialOptions(allowed);
        setFormData((prev) => ({
          ...prev,
          trialDays: allowed.includes(prev.trialDays) ? prev.trialDays : defaultDays,
        }));
        setConfigError("");
      } catch (error: any) {
        if (!active) return;
        setConfigError(error?.message || "Não foi possível carregar as opções de teste grátis.");
        setTrialOptions([]);
      } finally {
        if (active) setLoadingTrialConfig(false);
      }
    };
    bootstrap();
    return () => {
      active = false;
    };
  }, []);

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCpfCnpj(e.target.value);
    setFormData({ ...formData, document: formatted });

    if (formatted.length === 0) {
      setDocumentError("");
    } else if (formatted.length === 14 || formatted.length === 18) {
      if (!isValidCpfCnpj(formatted)) {
        setDocumentError("CPF ou CNPJ inválido");
      } else {
        setDocumentError("");
      }
    } else {
      setDocumentError("");
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");

    if (!isValidCpfCnpj(formData.document)) {
      setDocumentError("Por favor, insira um CPF ou CNPJ válido.");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setPasswordError("As senhas não coincidem.");
      return;
    }

    setPasswordError("");

    const phoneDigits = formData.phone.replace(/\D/g, "");
    if (phoneDigits.length < 10) {
      setPhoneError("Informe um telefone válido com DDD.");
      return;
    }
    setPhoneError("");

    try {
      setIsLoading(true);
      const cleanDocument = formData.document.replace(/\D/g, "");
      const trialDaysToSend =
        trialOptions.length === 1 ? trialOptions[0] : formData.trialDays;

      const response = await fetch("/api/public/tenant-registration", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          tenant_key: cleanDocument,
          display_name: formData.name,
          admin_name: formData.name,
          admin_email: formData.email,
          admin_password: formData.password,
          phone: phoneDigits,
          trial_days: trialDaysToSend,
        }),
      });

      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Erro ao criar conta. Tente novamente.");
      }

      setAccountCreated(true);
      setTimeout(() => {
        window.location.assign("/login");
      }, 2200);
    } catch (error: any) {
      setSubmitError(error.message || "Erro ao criar conta. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const trialGridClass =
    trialOptions.length === 1
      ? "grid grid-cols-1 gap-2"
      : trialOptions.length === 2
        ? "grid grid-cols-2 gap-2"
        : "grid grid-cols-3 gap-2";

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <SEO
        title="Criar conta grátis | RosTech"
        description="Comece seu teste grátis no melhor ERP para oficinas mecânicas do Brasil."
        canonicalUrl="/cadastro"
      />
      {accountCreated && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">Conta criada com sucesso!</h3>
            <p className="mt-2 text-sm text-slate-600">
              Seu teste grátis foi ativado. Vamos te levar para a tela de login.
            </p>
          </div>
        </div>
      )}
      <div className="flex-1 flex flex-col justify-center py-6 px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div>
            <Link to="/" className="flex items-center space-x-2 mb-6">
              <div className="bg-blue-700 text-white p-1.5 rounded">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                </svg>
              </div>
              <span className="text-xl font-black tracking-tight text-slate-900">AUTO<span className="text-rose-600">GESTOR</span></span>
            </Link>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900">
              Comece agora grátis
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Já tem uma conta?{" "}
              <Link to="/login" className="font-medium text-blue-700 hover:text-blue-600">
                Faça login
              </Link>
            </p>
          </div>

          <div className="mt-6">
            <form onSubmit={handleSignup} className="space-y-4">
              <Input
                label="Nome completo ou Razão Social"
                id="name"
                name="name"
                type="text"
                required
                placeholder="João da Silva"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />

              <Input
                label="CPF ou CNPJ"
                id="document"
                name="document"
                type="text"
                required
                placeholder="000.000.000-00"
                value={formData.document}
                onChange={handleDocumentChange}
                error={documentError}
                maxLength={18}
              />

              <Input
                label="E-mail profissional"
                id="email"
                name="email"
                type="email"
                required
                placeholder="voce@oficina.com.br"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />

              <Input
                label="Telefone / WhatsApp"
                id="phone"
                name="phone"
                type="tel"
                required
                placeholder="(00) 00000-0000"
                value={formData.phone}
                onChange={(e) => {
                  setFormData({ ...formData, phone: formatPhone(e.target.value) });
                  if (phoneError) setPhoneError("");
                }}
                error={phoneError}
                maxLength={15}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Senha"
                  id="password"
                  name="password"
                  type="password"
                  required
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />

                <Input
                  label="Confirmar senha"
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) => {
                    setFormData({ ...formData, confirmPassword: e.target.value });
                    if (formData.password && e.target.value !== formData.password) {
                      setPasswordError("As senhas não coincidem.");
                    } else {
                      setPasswordError("");
                    }
                  }}
                  error={passwordError}
                />
              </div>

              {loadingTrialConfig ? (
                <p className="text-sm text-slate-500">Carregando opções de teste grátis...</p>
              ) : configError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {configError}
                </div>
              ) : trialOptions.length === 1 ? (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-3">
                  <p className="text-sm font-semibold text-slate-800">
                    Teste grátis de {trialOptions[0]} dias
                  </p>
                  <p className="mt-1 text-xs text-slate-600">
                    Seu login ficará ativo somente durante esse período.
                  </p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Escolha o período do teste grátis
                  </label>
                  <div className={trialGridClass}>
                    {trialOptions.map((days) => (
                      <button
                        key={days}
                        type="button"
                        onClick={() => setFormData({ ...formData, trialDays: days })}
                        className={`rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                          formData.trialDays === days
                            ? "border-blue-700 bg-blue-700 text-white shadow-sm"
                            : "border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50"
                        }`}
                      >
                        {days} dias
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    Seu login ficará ativo somente durante o período escolhido.
                  </p>
                </div>
              )}

              {submitError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {submitError}
                </div>
              )}

              <div className="pt-1">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading || loadingTrialConfig || !!configError || trialOptions.length === 0}
                >
                  {isLoading ? "Criando conta..." : "Criar conta grátis"}
                </Button>
              </div>
            </form>
            <p className="mt-4 text-[11px] text-slate-500 text-center leading-relaxed">
              Ao se cadastrar, você concorda com nossos{" "}
              <Link to="/termos" className="font-medium text-blue-700 hover:text-blue-600">Termos de Uso</Link>{" "}
              e{" "}
              <Link to="/privacidade" className="font-medium text-blue-700 hover:text-blue-600">Política de Privacidade</Link>.
            </p>
            <div className="mt-4 flex justify-center">
              <Link to="/" className="flex items-center text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-slate-900 relative">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1621259182978-fbf93132d53d?auto=format&fit=crop&q=80&w=1600')] bg-cover bg-center opacity-40 mix-blend-overlay"></div>
        <div className="relative w-full flex items-center justify-center p-12 lg:p-24">
          <div className="max-w-md text-white">
            <h3 className="text-3xl font-black mb-8 leading-tight">O sistema operacional da sua oficina moderna.</h3>

            <div className="space-y-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-blue-400" />
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-bold">Implantação Assistida</h4>
                  <p className="mt-2 text-slate-300">Nossa equipe ajuda você a importar dados do seu sistema antigo sem custos adicionais.</p>
                </div>
              </div>
              <div className="flex">
                <div className="flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-blue-400" />
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-bold">Treinamento Gratuito</h4>
                  <p className="mt-2 text-slate-300">Acesso a academias em vídeo e suporte em tempo real para toda a sua equipe.</p>
                </div>
              </div>
              <div className="flex">
                <div className="flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-blue-400" />
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-bold">100% na Nuvem</h4>
                  <p className="mt-2 text-slate-300">Acesse de qualquer lugar, no computador ou no celular. Seus dados estão seguros e em backup constante.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
